import React, { useCallback, useEffect, useMemo, useState } from "react";
import AppLayout from "../components/AppLayout";
import StorageImage from "../components/StorageImage";
import { formatMoney as formatCurrency } from "../lib/currency";
import { supabase } from "../lib/supabase";
import {
  TENANT_DOCUMENT_BUCKET,
  TENANT_IMAGE_BUCKET,
  buildTenantAccountSnapshot,
  downloadTenantStatement,
  getTenantProfileItems,
  printTenantStatement,
  uploadTenantFile,
} from "../lib/tenants";
import { useToast } from "../contexts/ToastContext";
import { useAuth } from "../contexts/AuthContext";
import { normalizeRole } from "../lib/permissions";
import { motion } from "framer-motion";
import {
  Building2,
  Download,
  Mail,
  Phone,
  Plus,
  Printer,
  Search,
  Users,
  CalendarDays,
  Wallet,
  X,
  Pencil,
  Trash2,
  FileText,
  Image,
  ReceiptText,
} from "lucide-react";

function Button({ children, className = "", variant = "default", ...props }) {
  const base =
    "inline-flex items-center justify-center whitespace-nowrap transition-all font-medium disabled:pointer-events-none disabled:opacity-50";
  const styles =
    variant === "outline"
      ? "border"
      : "bg-orange-500 text-white hover:bg-orange-400 shadow-sm";

  return (
    <button className={`${base} ${styles} ${className}`} {...props}>
      {children}
    </button>
  );
}

function Input({ className = "", ...props }) {
  return (
    <input
      {...props}
      className={`flex w-full rounded-2xl border px-4 py-3 text-sm outline-none transition focus:border-sky-300 focus:ring-4 focus:ring-sky-100 ${className}`}
    />
  );
}

function Select({ className = "", children, ...props }) {
  return (
    <select
      {...props}
      className={`flex w-full rounded-2xl border px-4 py-3 text-sm outline-none transition focus:border-sky-300 focus:ring-4 focus:ring-sky-100 ${className}`}
    >
      {children}
    </select>
  );
}

function Badge({ children, className = "" }) {
  return (
    <span className={`inline-flex items-center rounded-full px-2.5 py-1 text-xs font-medium ${className}`}>
      {children}
    </span>
  );
}

function formatMoney(amount, currency) {
  return formatCurrency(amount, currency);
}

function getStatusClasses(status) {
  if (status === "Active") return "bg-emerald-100 text-emerald-700";
  if (status === "Notice") return "bg-sky-100 text-sky-700";
  return "bg-amber-100 text-amber-800";
}

function parseDocumentLinks(value) {
  return String(value || "")
    .split("\n")
    .map((item) => item.trim())
    .filter(Boolean);
}

function invoiceStatusClasses(status) {
  if (status === "Paid") return "bg-emerald-100 text-emerald-700";
  if (status === "Partial") return "bg-sky-100 text-sky-700";
  if (status === "Overdue") return "bg-rose-100 text-rose-700";
  return "bg-amber-100 text-amber-800";
}

function paymentStatusClasses(status) {
  if (status === "Paid") return "bg-emerald-100 text-emerald-700";
  if (status === "Pending") return "bg-sky-100 text-sky-700";
  return "bg-rose-100 text-rose-700";
}

function extractOrganizationId(profile, user, sessionClaims) {
  return (
    profile?.organization_id ||
    user?.app_metadata?.organization_id ||
    user?.app_metadata?.organizationId ||
    user?.user_metadata?.organization_id ||
    user?.user_metadata?.organizationId ||
    sessionClaims?.app_metadata?.organization_id ||
    sessionClaims?.app_metadata?.organizationId ||
    sessionClaims?.user_metadata?.organization_id ||
    sessionClaims?.user_metadata?.organizationId ||
    ""
  );
}

function TenantFormModal({
  isOpen,
  mode = "create",
  tenant = null,
  onClose,
  onSaved,
  isDark,
  currency,
  currentOrganizationId,
  isGrowthUser,
}) {
  const toast = useToast();
  const [propertyOptions, setPropertyOptions] = useState([]);
  const [form, setForm] = useState({
    full_name: "",
    property_name: "",
    unit: "",
    phone: "",
    email: "",
    monthly_rent: "",
    lease_start: "",
    lease_end: "",
    tenant_image_url: "",
    supporting_documents: "",
    status: "Active",
  });

  const [saving, setSaving] = useState(false);
  const [loadingProperties, setLoadingProperties] = useState(false);
  const [tenantImageFile, setTenantImageFile] = useState(null);
  const [documentFiles, setDocumentFiles] = useState([]);

  useEffect(() => {
    if (!isOpen) return;

    const fetchProperties = async () => {
      setLoadingProperties(true);

      if (!currentOrganizationId) {
        setPropertyOptions([]);
        setLoadingProperties(false);
        return;
      }

      let query = supabase
        .from("properties")
        .select("id, name, units")
        .order("name", { ascending: true });

      if (currentOrganizationId) {
        query = query.eq("organization_id", currentOrganizationId);
      }

      const { data, error } = await query;

      setLoadingProperties(false);

      if (error) {
        console.error("Error fetching properties for tenant form:", error);
        toast.error(
          "Properties could not load",
          "The property list is unavailable right now, but you can try again in a moment."
        );
        return;
      }

      setPropertyOptions((data || []).filter((property) => property.name));
    };

    fetchProperties();
  }, [currentOrganizationId, isGrowthUser, isOpen, toast]);

  useEffect(() => {
    if (tenant) {
      setForm({
        full_name: tenant.full_name || "",
        property_name: tenant.property_name || "",
        unit: tenant.unit || "",
        phone: tenant.phone || "",
        email: tenant.email || "",
        monthly_rent: tenant.monthly_rent ?? "",
        lease_start: tenant.lease_start || "",
        lease_end: tenant.lease_end || "",
        tenant_image_url: tenant.tenant_image_url || "",
        supporting_documents: tenant.supporting_documents || "",
        status: tenant.status || "Active",
      });
    } else {
      setForm({
        full_name: "",
        property_name: "",
        unit: "",
        phone: "",
        email: "",
        monthly_rent: "",
        lease_start: "",
        lease_end: "",
        tenant_image_url: "",
        supporting_documents: "",
        status: "Active",
      });
    }

    setTenantImageFile(null);
    setDocumentFiles([]);
  }, [tenant, isOpen]);

  if (!isOpen) return null;

  const update = (key, value) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  const propertyChoices = (() => {
    const names = propertyOptions.map((property) => property.name);

    if (form.property_name && !names.includes(form.property_name)) {
      return [{ id: "current-property", name: form.property_name }, ...propertyOptions];
    }

    return propertyOptions;
  })();

  const selectedProperty = propertyOptions.find((property) => property.name === form.property_name);
  const unitChoices = (() => {
    const unitCount = Math.max(0, Number(selectedProperty?.units || 0));
    const generatedUnits = Array.from({ length: unitCount }, (_, index) => `Unit ${index + 1}`);

    if (form.unit && !generatedUnits.includes(form.unit)) {
      return [form.unit, ...generatedUnits];
    }

    return generatedUnits;
  })();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);

    let tenantImageUrl = form.tenant_image_url;
    let supportingDocuments = form.supporting_documents;

    try {
      if (tenantImageFile) {
        tenantImageUrl = await uploadTenantFile(
          TENANT_IMAGE_BUCKET,
          tenant?.id,
          tenantImageFile
        );
      }

      if (documentFiles.length > 0) {
        const uploadedDocumentUrls = await Promise.all(
          documentFiles.map((file) =>
            uploadTenantFile(TENANT_DOCUMENT_BUCKET, tenant?.id, file)
          )
        );

        supportingDocuments = [
          ...parseDocumentLinks(form.supporting_documents),
          ...uploadedDocumentUrls,
        ].join("\n");
      }
    } catch (error) {
      setSaving(false);
      console.error("Tenant file upload error:", error);
      toast.error(
        "Tenant file upload failed",
        "Please check your storage bucket configuration and try again."
      );
      return;
    }

    const payload = {
      full_name: form.full_name,
      property_name: form.property_name,
      unit: form.unit,
      phone: form.phone,
      email: form.email,
      monthly_rent: Number(form.monthly_rent || 0),
      lease_start: form.lease_start || null,
      lease_end: form.lease_end || null,
      tenant_image_url: tenantImageUrl,
      supporting_documents: supportingDocuments,
      status: form.status,
      organization_id: tenant?.organization_id || currentOrganizationId || null,
    };

    let error;

    if (mode === "edit" && tenant?.id) {
      const result = await supabase.from("tenants").update(payload).eq("id", tenant.id);
      error = result.error;
    } else {
      const result = await supabase.from("tenants").insert([payload]);
      error = result.error;
    }

    setSaving(false);

    if (error) {
      console.error(`${mode} tenant error:`, error);
      toast.error(
        mode === "edit" ? "Tenant update failed" : "Tenant creation failed",
        "Please review the tenant details and try again."
      );
      return;
    }

    toast.success(
      mode === "edit" ? "Tenant updated" : "Tenant created",
      `${form.full_name} is now saved in SpaceM8.`
    );
    onSaved();
    onClose();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div
        className={`w-full max-w-2xl rounded-[2rem] border p-6 shadow-2xl ${
          isDark ? "border-slate-800 bg-slate-900" : "border-slate-200 bg-white"
        }`}
      >
        <div className="mb-6 flex items-center justify-between">
          <div>
            <h2 className={`text-2xl font-semibold ${isDark ? "text-white" : "text-slate-900"}`}>
              {mode === "edit" ? "Edit Tenant" : "Add Tenant"}
            </h2>
            <p className={`mt-1 text-sm ${isDark ? "text-slate-400" : "text-slate-500"}`}>
              {mode === "edit" ? "Update this tenant record." : "Create a new tenant record."}
            </p>
          </div>

          <button
            onClick={onClose}
            className={`rounded-2xl border p-3 transition ${
              isDark
                ? "border-slate-700 bg-slate-900 text-white hover:bg-slate-800"
                : "border-slate-200 bg-white text-slate-700 hover:bg-slate-50"
            }`}
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="grid gap-4 md:grid-cols-2">
          <div className="md:col-span-2">
            <p className={`text-xs font-semibold uppercase tracking-[0.18em] ${isDark ? "text-sky-400" : "text-sky-700"}`}>
              Resident Details
            </p>
          </div>

          <div>
            <label className={`mb-2 block text-sm font-medium ${isDark ? "text-slate-300" : "text-slate-700"}`}>
              Full name
            </label>
            <Input
              placeholder="Full name"
              value={form.full_name}
              onChange={(e) => update("full_name", e.target.value)}
              className={isDark ? "border-slate-700 bg-slate-950 text-white placeholder:text-slate-500" : "border-slate-200 bg-white text-slate-900"}
              required
            />
          </div>

          <div>
            <label className={`mb-2 block text-sm font-medium ${isDark ? "text-slate-300" : "text-slate-700"}`}>
              Property
            </label>
            <Select
              value={form.property_name}
              onChange={(e) =>
                setForm((prev) => ({
                  ...prev,
                  property_name: e.target.value,
                  unit: "",
                }))
              }
              className={isDark ? "border-slate-700 bg-slate-950 text-white" : "border-slate-200 bg-white text-slate-900"}
              required
              disabled={loadingProperties || propertyChoices.length === 0}
            >
              <option value="" disabled>
                {loadingProperties
                  ? "Loading properties..."
                  : propertyChoices.length === 0
                  ? "No properties available"
                  : "Select property"}
              </option>
              {propertyChoices.map((property) => (
                <option key={property.id} value={property.name}>
                  {property.name}
                </option>
              ))}
            </Select>
            <p className={`mt-2 text-xs ${isDark ? "text-slate-400" : "text-slate-500"}`}>
              {propertyChoices.length === 0
                ? "Add a property first so tenants can be assigned to it."
                : "Choose the property this tenant will occupy."}
            </p>
          </div>

          <div>
            <label className={`mb-2 block text-sm font-medium ${isDark ? "text-slate-300" : "text-slate-700"}`}>
              Unit
            </label>
            <Select
              value={form.unit}
              onChange={(e) => update("unit", e.target.value)}
              className={isDark ? "border-slate-700 bg-slate-950 text-white" : "border-slate-200 bg-white text-slate-900"}
              required
              disabled={!form.property_name || unitChoices.length === 0}
            >
              <option value="" disabled>
                {!form.property_name
                  ? "Select property first"
                  : unitChoices.length === 0
                  ? "No units configured"
                  : "Select unit"}
              </option>
              {unitChoices.map((unit) => (
                <option key={unit} value={unit}>
                  {unit}
                </option>
              ))}
            </Select>
            <p className={`mt-2 text-xs ${isDark ? "text-slate-400" : "text-slate-500"}`}>
              {!form.property_name
                ? "Choose a property to load its available units."
                : unitChoices.length === 0
                ? "This property has no unit count configured yet."
                : "Pick the unit this tenant will occupy."}
            </p>
          </div>

          <div>
            <label className={`mb-2 block text-sm font-medium ${isDark ? "text-slate-300" : "text-slate-700"}`}>
              Phone
            </label>
            <Input
              placeholder="Phone"
              value={form.phone}
              onChange={(e) => update("phone", e.target.value)}
              className={isDark ? "border-slate-700 bg-slate-950 text-white placeholder:text-slate-500" : "border-slate-200 bg-white text-slate-900"}
            />
          </div>

          <div>
            <label className={`mb-2 block text-sm font-medium ${isDark ? "text-slate-300" : "text-slate-700"}`}>
              Email
            </label>
            <Input
              type="email"
              placeholder="Email address"
              value={form.email}
              onChange={(e) => update("email", e.target.value)}
              className={isDark ? "border-slate-700 bg-slate-950 text-white placeholder:text-slate-500" : "border-slate-200 bg-white text-slate-900"}
            />
          </div>

          <div>
            <label className={`mb-2 block text-sm font-medium ${isDark ? "text-slate-300" : "text-slate-700"}`}>
              Monthly rent
            </label>
            <Input
              type="number"
              placeholder={`Monthly rent (${currency})`}
              value={form.monthly_rent}
              onChange={(e) => update("monthly_rent", e.target.value)}
              className={isDark ? "border-slate-700 bg-slate-950 text-white placeholder:text-slate-500" : "border-slate-200 bg-white text-slate-900"}
              required
            />
          </div>

          <div className="md:col-span-2 mt-2">
            <p className={`text-xs font-semibold uppercase tracking-[0.18em] ${isDark ? "text-sky-400" : "text-sky-700"}`}>
              Tenancy Details
            </p>
          </div>

          <div>
            <label className={`mb-2 block text-sm font-medium ${isDark ? "text-slate-300" : "text-slate-700"}`}>
              Lease start
            </label>
            <Input
              type="date"
              value={form.lease_start}
              onChange={(e) => update("lease_start", e.target.value)}
              className={isDark ? "border-slate-700 bg-slate-950 text-white" : "border-slate-200 bg-white text-slate-900"}
            />
          </div>

          <div>
            <label className={`mb-2 block text-sm font-medium ${isDark ? "text-slate-300" : "text-slate-700"}`}>
              Lease end
            </label>
            <Input
              type="date"
              value={form.lease_end}
              onChange={(e) => update("lease_end", e.target.value)}
              className={isDark ? "border-slate-700 bg-slate-950 text-white" : "border-slate-200 bg-white text-slate-900"}
            />
          </div>

          <div className="md:col-span-2 mt-2">
            <p className={`text-xs font-semibold uppercase tracking-[0.18em] ${isDark ? "text-sky-400" : "text-sky-700"}`}>
              Documents And Status
            </p>
          </div>

          <div className="md:col-span-2">
            <label className={`mb-2 block text-sm font-medium ${isDark ? "text-slate-300" : "text-slate-700"}`}>
              Tenant image
            </label>
            <Input
              type="file"
              accept="image/*"
              onChange={(e) => setTenantImageFile(e.target.files?.[0] || null)}
              className={isDark ? "border-slate-700 bg-slate-950 text-white placeholder:text-slate-500" : "border-slate-200 bg-white text-slate-900"}
            />
            <p className={`mt-2 text-xs ${isDark ? "text-slate-400" : "text-slate-500"}`}>
              {tenantImageFile
                ? `Ready to upload: ${tenantImageFile.name}`
                : form.tenant_image_url
                ? "Current image is saved. Choose a new file to replace it."
                : "Attach a tenant photo from your device."}
            </p>
          </div>

          <div className="md:col-span-2">
            <label className={`mb-2 block text-sm font-medium ${isDark ? "text-slate-300" : "text-slate-700"}`}>
              Supporting documents
            </label>
            <Input
              type="file"
              multiple
              accept=".pdf,.doc,.docx,.png,.jpg,.jpeg,.webp,.txt"
              onChange={(e) => setDocumentFiles(Array.from(e.target.files || []))}
              className={isDark ? "border-slate-700 bg-slate-950 text-white placeholder:text-slate-500" : "border-slate-200 bg-white text-slate-900"}
            />
            <p className={`mt-2 text-xs ${isDark ? "text-slate-400" : "text-slate-500"}`}>
              {documentFiles.length > 0
                ? `${documentFiles.length} document${documentFiles.length === 1 ? "" : "s"} ready to upload.`
                : "Attach IDs, leases, receipts, or other tenant documents."}
            </p>
            {parseDocumentLinks(form.supporting_documents).length > 0 && (
              <div className={`mt-3 rounded-2xl border px-4 py-3 text-xs ${
                isDark ? "border-slate-700 bg-slate-950 text-slate-300" : "border-slate-200 bg-slate-50 text-slate-600"
              }`}>
                {parseDocumentLinks(form.supporting_documents).length} saved document
                {parseDocumentLinks(form.supporting_documents).length === 1 ? "" : "s"} on this tenant.
              </div>
            )}
          </div>

          <div className="md:col-span-2">
            <label className={`mb-2 block text-sm font-medium ${isDark ? "text-slate-300" : "text-slate-700"}`}>
              Tenant status
            </label>
            <Select
              value={form.status}
              onChange={(e) => update("status", e.target.value)}
              className={isDark ? "border-slate-700 bg-slate-950 text-white" : "border-slate-200 bg-white text-slate-900"}
            >
              <option>Active</option>
              <option>Notice</option>
              <option>Late</option>
            </Select>
          </div>

          <div className="mt-2 flex items-center justify-end gap-3 md:col-span-2">
            <button
              type="button"
              onClick={onClose}
              className={`inline-flex h-12 items-center justify-center rounded-2xl border px-5 font-medium transition ${
                isDark
                  ? "border-slate-700 bg-slate-900 text-white hover:bg-slate-800"
                  : "border-slate-200 bg-white text-slate-700 hover:bg-slate-50"
              }`}
            >
              Cancel
            </button>

            <button
              type="submit"
              disabled={saving}
              className={`inline-flex h-12 items-center justify-center rounded-2xl px-5 font-medium text-white transition ${
                saving ? "bg-orange-300" : "bg-orange-500 hover:bg-orange-600"
              }`}
            >
              {saving ? (mode === "edit" ? "Uploading..." : "Saving...") : mode === "edit" ? "Update Tenant" : "Create Tenant"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function DeleteTenantModal({ isOpen, tenant, onClose, onDeleted, isDark }) {
  const toast = useToast();
  const [deleting, setDeleting] = useState(false);

  if (!isOpen || !tenant) return null;

  const handleDelete = async () => {
    setDeleting(true);

    const { error } = await supabase.from("tenants").delete().eq("id", tenant.id);

    setDeleting(false);

    if (error) {
      console.error("Delete tenant error:", error);
      toast.error("Tenant deletion failed", "Please try again in a moment.");
      return;
    }

    toast.success("Tenant deleted", `${tenant.full_name} has been removed from SpaceM8.`);
    onDeleted();
    onClose();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div
        className={`w-full max-w-lg rounded-[2rem] border p-6 shadow-2xl ${
          isDark ? "border-slate-800 bg-slate-900" : "border-slate-200 bg-white"
        }`}
      >
        <h2 className={`text-2xl font-semibold ${isDark ? "text-white" : "text-slate-900"}`}>
          Delete Tenant
        </h2>
        <p className={`mt-2 text-sm leading-7 ${isDark ? "text-slate-400" : "text-slate-600"}`}>
          Are you sure you want to delete{" "}
          <span className={`font-semibold ${isDark ? "text-white" : "text-slate-900"}`}>
            {tenant.full_name}
          </span>
          ?
        </p>

        <div className="mt-6 flex items-center justify-end gap-3">
          <button
            onClick={onClose}
            className={`inline-flex h-12 items-center justify-center rounded-2xl border px-5 font-medium transition ${
              isDark
                ? "border-slate-700 bg-slate-900 text-white hover:bg-slate-800"
                : "border-slate-200 bg-white text-slate-700 hover:bg-slate-50"
            }`}
          >
            Cancel
          </button>

          <button
            onClick={handleDelete}
            disabled={deleting}
            className={`inline-flex h-12 items-center justify-center rounded-2xl px-5 font-medium text-white transition ${
              deleting ? "bg-red-300" : "bg-red-500 hover:bg-red-600"
            }`}
          >
            {deleting ? "Deleting..." : "Delete Tenant"}
          </button>
        </div>
      </div>
    </div>
  );
}

function TenantProfileModal({
  isOpen,
  tenant,
  onClose,
  isDark,
  currency,
  currentOrganizationId,
  isGrowthUser,
}) {
  const toast = useToast();
  const [accountData, setAccountData] = useState({
    invoices: [],
    payments: [],
  });
  const [loadingAccount, setLoadingAccount] = useState(false);

  useEffect(() => {
    if (!isOpen || !tenant) return;

    const fetchTenantAccount = async () => {
      setLoadingAccount(true);

      if (!currentOrganizationId) {
        setAccountData({
          invoices: [],
          payments: [],
        });
        setLoadingAccount(false);
        return;
      }

      let invoicesQuery = supabase
        .from("invoices")
        .select("id, tenant, property, unit, amount, issue_date, due_date, status, delivery")
        .eq("tenant", tenant.full_name)
        .order("due_date", { ascending: false });

      let paymentsQuery = supabase
        .from("payments")
        .select("id, invoice_id, tenant_name, property_name, unit, amount, due_date, status, payment_method, created_at")
        .eq("tenant_name", tenant.full_name)
        .order("created_at", { ascending: false });

      if (currentOrganizationId) {
        invoicesQuery = invoicesQuery.eq("organization_id", currentOrganizationId);
        paymentsQuery = paymentsQuery.eq("organization_id", currentOrganizationId);
      }

      const [{ data: invoiceData, error: invoiceError }, { data: paymentData, error: paymentError }] =
        await Promise.all([
          invoicesQuery,
          paymentsQuery,
        ]);

      if (invoiceError) console.error("Error fetching tenant invoices:", invoiceError);
      if (paymentError) console.error("Error fetching tenant payments:", paymentError);

      const matchesTenantSpace = (item, propertyKey, unitKey) => {
        const propertyMatches = !tenant.property_name || item[propertyKey] === tenant.property_name;
        const unitMatches = !tenant.unit || String(item[unitKey] || "") === String(tenant.unit || "");
        return propertyMatches && unitMatches;
      };

      setAccountData({
        invoices: (invoiceData || []).filter((invoice) => matchesTenantSpace(invoice, "property", "unit")),
        payments: (paymentData || []).filter((payment) => matchesTenantSpace(payment, "property_name", "unit")),
      });
      setLoadingAccount(false);
    };

    fetchTenantAccount();
  }, [currentOrganizationId, isGrowthUser, isOpen, tenant]);

  if (!isOpen || !tenant) return null;

  const { documents, invoiceLedger, accountSummary } = buildTenantAccountSnapshot(tenant, accountData);
  const profileItems = getTenantProfileItems(tenant, currency);

  return (
    <div className="fixed inset-0 z-[10010] flex items-center justify-center bg-slate-950/70 p-4 backdrop-blur-[3px]">
      <div
        style={{ scrollbarWidth: "none", msOverflowStyle: "none" }}
        className={`max-h-[92vh] w-full max-w-5xl overflow-y-auto rounded-[2rem] border p-5 text-sm shadow-2xl [&::-webkit-scrollbar]:hidden ${
          isDark ? "border-slate-800 bg-slate-900" : "border-slate-200 bg-white"
        }`}
      >
        <div className="mb-4 flex items-start justify-between gap-4">
          <div className="flex min-w-0 items-center gap-4">
            <div
              className={`flex h-16 w-16 shrink-0 items-center justify-center overflow-hidden rounded-2xl border ${
                isDark ? "border-slate-700 bg-slate-800" : "border-slate-200 bg-slate-50"
              }`}
            >
              {tenant.tenant_image_url ? (
                <StorageImage
                  src={tenant.tenant_image_url}
                  alt={tenant.full_name}
                  className="h-full w-full object-cover"
                />
              ) : (
                <Image className={`h-8 w-8 ${isDark ? "text-slate-500" : "text-slate-400"}`} />
              )}
            </div>

            <div className="min-w-0">
              <div className="flex flex-wrap items-center gap-2">
                <h2 className={`break-words text-xl font-semibold ${isDark ? "text-white" : "text-slate-900"}`}>
                  {tenant.full_name}
                </h2>
                <Badge className={getStatusClasses(tenant.status)}>{tenant.status}</Badge>
              </div>
              <p className={`mt-1 text-xs ${isDark ? "text-slate-400" : "text-slate-500"}`}>
                Tenant profile, lease snapshot, and account history.
              </p>
            </div>
          </div>

          <button
            onClick={onClose}
            className={`rounded-2xl border p-3 transition ${
              isDark
                ? "border-slate-700 bg-slate-900 text-white hover:bg-slate-800"
                : "border-slate-200 bg-white text-slate-700 hover:bg-slate-50"
            }`}
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="grid gap-2 md:grid-cols-4">
          {profileItems.map((item) => (
            <div
              key={item.label}
              className={`min-w-0 rounded-2xl border px-3 py-2.5 ${
                isDark ? "border-slate-700 bg-slate-800" : "border-slate-200 bg-slate-50"
              }`}
            >
              <p className={`text-xs uppercase tracking-wide ${isDark ? "text-slate-400" : "text-slate-500"}`}>
                {item.label}
              </p>
              <p className={`mt-1 break-words text-sm font-semibold ${isDark ? "text-white" : "text-slate-900"}`}>
                {item.value}
              </p>
            </div>
          ))}
        </div>

        <div className="mt-4 grid gap-2 md:grid-cols-4">
          {[
            { label: "Invoiced", value: formatMoney(accountSummary.invoiced, currency), tone: "bg-sky-100 text-sky-700" },
            { label: "Paid", value: formatMoney(accountSummary.paid, currency), tone: "bg-emerald-100 text-emerald-700" },
            { label: "Balance", value: formatMoney(accountSummary.balance, currency), tone: "bg-amber-100 text-amber-800" },
            { label: "Overdue", value: formatMoney(accountSummary.overdue, currency), tone: accountSummary.overdue > 0 ? "bg-rose-100 text-rose-700" : "bg-emerald-100 text-emerald-700" },
          ].map((item) => (
            <div
              key={item.label}
              className={`rounded-2xl border p-3 ${isDark ? "border-slate-700 bg-slate-800" : "border-slate-200 bg-slate-50"}`}
            >
              <Badge className={item.tone}>{item.label}</Badge>
              <p className={`mt-2 break-words text-base font-semibold ${isDark ? "text-white" : "text-slate-900"}`}>
                {loadingAccount ? "Loading..." : item.value}
              </p>
            </div>
          ))}
        </div>

        <div className="mt-4 grid gap-4 lg:grid-cols-2">
          <section
            className={`rounded-2xl border p-3 ${
              isDark ? "border-slate-700 bg-slate-800" : "border-slate-200 bg-slate-50"
            }`}
          >
            <div className="flex items-center gap-2">
              <FileText className={`h-4 w-4 ${isDark ? "text-slate-400" : "text-slate-500"}`} />
              <p className={`text-sm font-semibold ${isDark ? "text-white" : "text-slate-900"}`}>
                Invoice history
              </p>
            </div>

            {loadingAccount ? (
              <p className={`mt-3 text-sm ${isDark ? "text-slate-400" : "text-slate-500"}`}>
                Loading invoices...
              </p>
            ) : invoiceLedger.length === 0 ? (
              <p className={`mt-3 text-sm ${isDark ? "text-slate-400" : "text-slate-500"}`}>
                No invoices found for this tenant.
              </p>
            ) : (
              <div className="mt-3 space-y-2">
                {invoiceLedger.slice(0, 4).map((invoice) => (
                  <div
                    key={invoice.id}
                    className={`rounded-2xl border p-2.5 ${isDark ? "border-slate-700 bg-slate-900" : "border-slate-200 bg-white"}`}
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <p className={`break-words text-sm font-semibold ${isDark ? "text-white" : "text-slate-900"}`}>
                          {invoice.id}
                        </p>
                        <p className={`mt-1 text-xs ${isDark ? "text-slate-400" : "text-slate-500"}`}>
                          Due {invoice.due_date || "not set"}
                          {invoice.daysPastDue > 0 && invoice.balance > 0 ? ` - ${invoice.daysPastDue} days overdue` : ""}
                        </p>
                      </div>
                      <Badge className={invoiceStatusClasses(invoice.status)}>{invoice.status}</Badge>
                    </div>
                    <div className={`mt-3 grid gap-2 text-xs ${isDark ? "text-slate-300" : "text-slate-600"}`}>
                      <div className="flex justify-between gap-3">
                        <span>Amount</span>
                        <strong>{formatMoney(invoice.amount, currency)}</strong>
                      </div>
                      <div className="flex justify-between gap-3">
                        <span>Paid</span>
                        <strong>{formatMoney(invoice.paidAmount, currency)}</strong>
                      </div>
                      <div className="flex justify-between gap-3">
                        <span>Balance</span>
                        <strong>{formatMoney(invoice.balance, currency)}</strong>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </section>

          <section
            className={`rounded-2xl border p-3 ${
              isDark ? "border-slate-700 bg-slate-800" : "border-slate-200 bg-slate-50"
            }`}
          >
            <div className="flex items-center gap-2">
              <ReceiptText className={`h-4 w-4 ${isDark ? "text-slate-400" : "text-slate-500"}`} />
              <p className={`text-sm font-semibold ${isDark ? "text-white" : "text-slate-900"}`}>
                Payment history
              </p>
            </div>

            {loadingAccount ? (
              <p className={`mt-3 text-sm ${isDark ? "text-slate-400" : "text-slate-500"}`}>
                Loading payments...
              </p>
            ) : accountData.payments.length === 0 ? (
              <p className={`mt-3 text-sm ${isDark ? "text-slate-400" : "text-slate-500"}`}>
                No payments found for this tenant.
              </p>
            ) : (
              <div className="mt-3 space-y-2">
                {accountData.payments.slice(0, 4).map((payment) => (
                  <div
                    key={payment.id}
                    className={`rounded-2xl border p-2.5 ${isDark ? "border-slate-700 bg-slate-900" : "border-slate-200 bg-white"}`}
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <p className={`break-words text-sm font-semibold ${isDark ? "text-white" : "text-slate-900"}`}>
                          {formatMoney(Number(payment.amount || 0), currency)}
                        </p>
                        <p className={`mt-1 text-xs ${isDark ? "text-slate-400" : "text-slate-500"}`}>
                          {payment.invoice_id || "Unlinked"} - {payment.payment_method || "Method not set"}
                        </p>
                      </div>
                      <Badge className={paymentStatusClasses(payment.status)}>{payment.status}</Badge>
                    </div>
                    <p className={`mt-2 text-xs ${isDark ? "text-slate-400" : "text-slate-500"}`}>
                      Date {payment.due_date || payment.created_at?.slice(0, 10) || "not set"}
                    </p>
                  </div>
                ))}
              </div>
            )}
          </section>
        </div>

        <div
          className={`mt-4 rounded-2xl border p-3 ${
            isDark ? "border-slate-700 bg-slate-800" : "border-slate-200 bg-slate-50"
          }`}
        >
          <div className="flex items-center gap-2">
            <FileText className={`h-4 w-4 ${isDark ? "text-slate-400" : "text-slate-500"}`} />
            <p className={`text-sm font-semibold ${isDark ? "text-white" : "text-slate-900"}`}>
              Supporting documents
            </p>
          </div>

          {documents.length === 0 ? (
            <p className={`mt-3 text-sm ${isDark ? "text-slate-400" : "text-slate-500"}`}>
              No supporting documents added.
            </p>
          ) : (
            <div className="mt-3 grid gap-2">
              {documents.map((documentUrl, index) => (
                <a
                  key={`${documentUrl}-${index}`}
                  href={documentUrl}
                  target="_blank"
                  rel="noreferrer"
                  className={`break-words rounded-2xl border px-4 py-3 text-sm font-medium transition ${
                    isDark
                      ? "border-slate-700 bg-slate-900 text-sky-300 hover:bg-slate-950"
                      : "border-slate-200 bg-white text-sky-700 hover:bg-sky-50"
                  }`}
                >
                  Document {index + 1}: {documentUrl}
                </a>
              ))}
            </div>
          )}
        </div>

        <div className="mt-4 flex flex-wrap justify-end gap-2">
          <button
            type="button"
            onClick={() =>
              printTenantStatement(
                tenant,
                accountSummary,
                invoiceLedger,
                accountData.payments,
                documents,
                currency,
                () =>
                  toast.error(
                    "Tenant statement could not open",
                    "Please allow pop-ups to print or save this statement."
                  )
              )
            }
            className={`inline-flex h-11 items-center justify-center rounded-2xl border px-4 text-sm font-medium transition ${
              isDark
                ? "border-slate-700 bg-slate-900 text-white hover:bg-slate-800"
                : "border-slate-200 bg-white text-slate-700 hover:bg-slate-50"
            }`}
          >
            <Printer className="mr-2 h-4 w-4" />
            Print / PDF
          </button>

          <button
            type="button"
            onClick={() => downloadTenantStatement(tenant, accountSummary, invoiceLedger, accountData.payments, documents, currency)}
            className="inline-flex h-11 items-center justify-center rounded-2xl bg-orange-500 px-4 text-sm font-medium text-white transition hover:bg-orange-600"
          >
            <Download className="mr-2 h-4 w-4" />
            Save
          </button>

          <button
            onClick={onClose}
            className={`inline-flex h-11 items-center justify-center rounded-2xl border px-4 text-sm font-medium transition ${
              isDark
                ? "border-slate-700 bg-slate-900 text-white hover:bg-slate-800"
                : "border-slate-200 bg-white text-slate-700 hover:bg-slate-50"
            }`}
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
}

function LeaseModal({ isOpen, tenant, onClose, onSaved, isDark, currency }) {
  const toast = useToast();
  const [form, setForm] = useState({
    property_name: "",
    unit: "",
    monthly_rent: "",
    lease_start: "",
    lease_end: "",
    status: "Active",
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (tenant) {
      setForm({
        property_name: tenant.property_name || "",
        unit: tenant.unit || "",
        monthly_rent: tenant.monthly_rent ?? "",
        lease_start: tenant.lease_start || "",
        lease_end: tenant.lease_end || "",
        status: tenant.status || "Active",
      });
    }
  }, [tenant, isOpen]);

  if (!isOpen || !tenant) return null;

  const update = (key, value) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);

    const payload = {
      property_name: form.property_name,
      unit: form.unit,
      monthly_rent: Number(form.monthly_rent || 0),
      lease_start: form.lease_start || null,
      lease_end: form.lease_end || null,
      status: form.status,
    };

    const { error } = await supabase.from("tenants").update(payload).eq("id", tenant.id);

    setSaving(false);

    if (error) {
      console.error("Lease update error:", error);
      toast.error("Lease update failed", "Please review the lease details and try again.");
      return;
    }

    toast.success("Lease updated", `${tenant.full_name}'s lease details are now up to date.`);
    onSaved();
    onClose();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div
        className={`max-h-[90vh] w-full max-w-2xl overflow-y-auto rounded-[2rem] border p-6 shadow-2xl ${
          isDark ? "border-slate-800 bg-slate-900" : "border-slate-200 bg-white"
        }`}
      >
        <div className="mb-6 flex items-start justify-between gap-4">
          <div>
            <h2 className={`text-2xl font-semibold ${isDark ? "text-white" : "text-slate-900"}`}>
              Manage Lease
            </h2>
            <p className={`mt-1 text-sm ${isDark ? "text-slate-400" : "text-slate-500"}`}>
              {tenant.full_name}
            </p>
          </div>

          <button
            onClick={onClose}
            className={`rounded-2xl border p-3 transition ${
              isDark
                ? "border-slate-700 bg-slate-900 text-white hover:bg-slate-800"
                : "border-slate-200 bg-white text-slate-700 hover:bg-slate-50"
            }`}
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="grid gap-4 md:grid-cols-2">
          <Input
            placeholder="Property name"
            value={form.property_name}
            onChange={(e) => update("property_name", e.target.value)}
            className={isDark ? "border-slate-700 bg-slate-950 text-white placeholder:text-slate-500" : "border-slate-200 bg-white text-slate-900"}
            required
          />

          <Input
            placeholder="Unit"
            value={form.unit}
            onChange={(e) => update("unit", e.target.value)}
            className={isDark ? "border-slate-700 bg-slate-950 text-white placeholder:text-slate-500" : "border-slate-200 bg-white text-slate-900"}
            required
          />

          <Input
            type="number"
            placeholder={`Monthly rent (${currency})`}
            value={form.monthly_rent}
            onChange={(e) => update("monthly_rent", e.target.value)}
            className={isDark ? "border-slate-700 bg-slate-950 text-white placeholder:text-slate-500" : "border-slate-200 bg-white text-slate-900"}
            required
          />

          <Select
            value={form.status}
            onChange={(e) => update("status", e.target.value)}
            className={isDark ? "border-slate-700 bg-slate-950 text-white" : "border-slate-200 bg-white text-slate-900"}
          >
            <option>Active</option>
            <option>Notice</option>
            <option>Late</option>
          </Select>

          <div>
            <label className={`mb-2 block text-sm font-medium ${isDark ? "text-slate-300" : "text-slate-700"}`}>
              Lease start
            </label>
            <Input
              type="date"
              value={form.lease_start}
              onChange={(e) => update("lease_start", e.target.value)}
              className={isDark ? "border-slate-700 bg-slate-950 text-white" : "border-slate-200 bg-white text-slate-900"}
            />
          </div>

          <div>
            <label className={`mb-2 block text-sm font-medium ${isDark ? "text-slate-300" : "text-slate-700"}`}>
              Lease end
            </label>
            <Input
              type="date"
              value={form.lease_end}
              onChange={(e) => update("lease_end", e.target.value)}
              className={isDark ? "border-slate-700 bg-slate-950 text-white" : "border-slate-200 bg-white text-slate-900"}
            />
          </div>

          <div
            className={`rounded-2xl border px-4 py-3 text-sm md:col-span-2 ${
              isDark
                ? "border-slate-700 bg-slate-950 text-slate-300"
                : "border-slate-200 bg-slate-50 text-slate-600"
            }`}
          >
            Current lease value:
            <span className={`ml-2 font-semibold ${isDark ? "text-white" : "text-slate-900"}`}>
              {formatMoney(Number(form.monthly_rent || 0), currency)}
            </span>
          </div>

          <div className="mt-2 flex items-center justify-end gap-3 md:col-span-2">
            <button
              type="button"
              onClick={onClose}
              className={`inline-flex h-12 items-center justify-center rounded-2xl border px-5 font-medium transition ${
                isDark
                  ? "border-slate-700 bg-slate-900 text-white hover:bg-slate-800"
                  : "border-slate-200 bg-white text-slate-700 hover:bg-slate-50"
              }`}
            >
              Cancel
            </button>

            <button
              type="submit"
              disabled={saving}
              className={`inline-flex h-12 items-center justify-center rounded-2xl px-5 font-medium text-white transition ${
                saving ? "bg-orange-300" : "bg-orange-500 hover:bg-orange-600"
              }`}
            >
              {saving ? "Updating..." : "Update Lease"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function TenantCard({ tenant, index, isDark, currency, onViewTenant, onManageLease, onEdit, onDelete }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 18 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.35, delay: index * 0.05 }}
      className={`rounded-[2rem] border shadow-md transition-all hover:-translate-y-0.5 hover:shadow-lg ${
        isDark ? "border-slate-800 bg-slate-900" : "border-slate-200 bg-white"
      }`}
    >
      <div className="p-5">
        <div className="flex items-start justify-between gap-4">
          <div>
            <div className="flex items-center gap-2">
              <h3 className={`text-lg font-semibold ${isDark ? "text-white" : "text-slate-900"}`}>
                {tenant.full_name}
              </h3>
              <Badge className={getStatusClasses(tenant.status)}>{tenant.status}</Badge>
            </div>

            <div className={`mt-2 flex flex-wrap items-center gap-3 text-sm ${isDark ? "text-slate-400" : "text-slate-500"}`}>
              <span className="inline-flex items-center gap-1.5">
                <Building2 className="h-4 w-4" />
                {tenant.property_name}
              </span>
              <span>Unit {tenant.unit}</span>
            </div>
          </div>

          <div className="flex items-center gap-2">
            <button
              onClick={() => onEdit(tenant)}
              className={`rounded-xl border p-2 transition ${
                isDark
                  ? "border-slate-700 text-slate-400 hover:bg-slate-800 hover:text-white"
                  : "border-slate-200 text-slate-500 hover:bg-slate-50 hover:text-slate-700"
              }`}
            >
              <Pencil className="h-4 w-4" />
            </button>

            <button
              onClick={() => onDelete(tenant)}
              className={`rounded-xl border p-2 transition ${
                isDark
                  ? "border-slate-700 text-slate-400 hover:bg-slate-800 hover:text-red-400"
                  : "border-slate-200 text-slate-500 hover:bg-slate-50 hover:text-red-600"
              }`}
            >
              <Trash2 className="h-4 w-4" />
            </button>
          </div>
        </div>

        <div className="mt-5 grid items-stretch gap-3 md:grid-cols-2 xl:grid-cols-4">
          <div className={`min-h-[92px] min-w-0 rounded-2xl border px-4 py-3 ${isDark ? "border-slate-700 bg-slate-800" : "border-slate-200 bg-slate-50"}`}>
            <p className={`text-xs uppercase tracking-wide ${isDark ? "text-slate-400" : "text-slate-500"}`}>
              Monthly rent
            </p>
            <p className={`mt-1  break-all text-sm font-semibold ${isDark ? "text-white" : "text-slate-900"}`}>
              {formatMoney(Number(tenant.monthly_rent || 0), currency)}
            </p>
          </div>

          <div className={`min-h-[92px] min-w-0 rounded-2xl border px-4 py-3 ${isDark ? "border-slate-700 bg-slate-800" : "border-slate-200 bg-slate-50"}`}>
            <p className={`text-xs uppercase tracking-wide ${isDark ? "text-slate-400" : "text-slate-500"}`}>
              Phone
            </p>
            <p className={`mt-1 break-words text-sm font-semibold leading-6 ${isDark ? "text-white" : "text-slate-900"}`}>
              {tenant.phone || "—"}
            </p>
          </div>

          <div className={`min-h-[92px] min-w-0 rounded-2xl border px-4 py-3 ${isDark ? "border-slate-700 bg-slate-800" : "border-slate-200 bg-slate-50"}`}>
            <p className={`text-xs uppercase tracking-wide ${isDark ? "text-slate-400" : "text-slate-500"}`}>
              Lease start
            </p>
            <p className={`mt-1 font-semibold ${isDark ? "text-white" : "text-slate-900"}`}>
              {tenant.lease_start || "—"}
            </p>
          </div>

          <div className={`min-w-0 rounded-2xl border px-4 py-3 ${isDark ? "border-slate-700 bg-slate-800" : "border-slate-200 bg-slate-50"}`}>
            <p className={`text-xs uppercase tracking-wide ${isDark ? "text-slate-400" : "text-slate-500"}`}>
              Lease end
            </p>
            <p className={`mt-1 font-semibold ${isDark ? "text-white" : "text-slate-900"}`}>
              {tenant.lease_end || "—"}
            </p>
          </div>
        </div>

        <div className={`mt-5 flex flex-col gap-3 border-t pt-4 md:flex-row md:items-center md:justify-between ${isDark ? "border-slate-800" : "border-slate-200"}`}>
          <div className={`flex flex-wrap items-center gap-4 text-sm ${isDark ? "text-slate-300" : "text-slate-600"}`}>
            <span className="inline-flex min-w-0 items-center gap-1.5">
              <Phone className="h-4 w-4" />
              <span className="break-words">{tenant.phone || "No phone"}</span>
            </span>
            <span className="inline-flex min-w-0 items-center gap-1.5">
              <Mail className="h-4 w-4" />
              <span className="break-words">{tenant.email || "No email"}</span>
            </span>
          </div>

          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              onClick={() => onViewTenant(tenant)}
              className={`rounded-2xl px-4 py-2 text-sm ${
                isDark
                  ? "border-slate-700 bg-slate-900 text-white hover:bg-slate-800"
                  : "border-slate-200 bg-white text-slate-700 hover:bg-slate-50"
              }`}
            >
              View tenant
            </Button>

            <Button
              onClick={() => onManageLease(tenant)}
              className="rounded-2xl px-4 py-2 text-sm"
            >
              Manage lease
            </Button>
          </div>
        </div>
      </div>
    </motion.div>
  );
}

export default function TenantsPage({
  isDark,
  setIsDark,
  currency,
  setCurrency,
}) {
  const { profile, user, sessionClaims } = useAuth();
  const [runtimeAuthUser, setRuntimeAuthUser] = useState(null);
  const [runtimeProfile, setRuntimeProfile] = useState(null);
  const [tenants, setTenants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("All");
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [viewingTenant, setViewingTenant] = useState(null);
  const [managingLease, setManagingLease] = useState(null);
  const [editingTenant, setEditingTenant] = useState(null);
  const [deletingTenant, setDeletingTenant] = useState(null);
  const effectiveUser = user || runtimeAuthUser;
  const effectiveProfile = profile || runtimeProfile;

  useEffect(() => {
    let isMounted = true;

    if (user && profile) {
      setRuntimeAuthUser(null);
      setRuntimeProfile(null);
      return undefined;
    }

    const loadRuntimeUser = async () => {
      const {
        data: { user: authUser },
      } = await supabase.auth.getUser();

      if (!isMounted) return;

      setRuntimeAuthUser(authUser || null);

      if (!authUser) {
        setRuntimeProfile(null);
        return;
      }

      let nextProfile = null;

      const { data: profileById } = await supabase
        .from("user_profiles")
        .select("*")
        .eq("id", authUser.id)
        .maybeSingle();

      nextProfile = profileById || null;

      if (!nextProfile && authUser.email) {
        const { data: profileByEmail } = await supabase
          .from("user_profiles")
          .select("*")
          .eq("email", authUser.email)
          .maybeSingle();

        nextProfile = profileByEmail || null;
      }

      if (!isMounted) return;
      setRuntimeProfile(nextProfile);
    };

    loadRuntimeUser();

    return () => {
      isMounted = false;
    };
  }, [profile, user]);

  const normalizedRole = normalizeRole(
    effectiveProfile?.role ||
      effectiveUser?.app_metadata?.role ||
      effectiveUser?.app_metadata?.app_role ||
      effectiveUser?.user_metadata?.role ||
      effectiveUser?.user_metadata?.app_role ||
      sessionClaims?.app_metadata?.role ||
      sessionClaims?.app_metadata?.app_role ||
      sessionClaims?.user_metadata?.role ||
      sessionClaims?.user_metadata?.app_role
  );
  const isGrowthUser = normalizedRole === "Growth";
  const currentOrganizationId = extractOrganizationId(effectiveProfile, effectiveUser, sessionClaims);

  const fetchTenants = useCallback(async () => {
    setLoading(true);

    if (!currentOrganizationId) {
      setTenants([]);
      setLoading(false);
      return;
    }

    let query = supabase
      .from("tenants")
      .select("*")
      .order("created_at", { ascending: false });

    if (currentOrganizationId) {
      query = query.eq("organization_id", currentOrganizationId);
    }

    const { data, error } = await query;

    if (error) {
      console.error("Error fetching tenants:", error);
    } else {
      setTenants(data || []);
    }

    setLoading(false);
  }, [currentOrganizationId]);

  useEffect(() => {
    fetchTenants();
  }, [fetchTenants]);

  const totals = useMemo(() => {
    const active = tenants.filter((item) => item.status === "Active").length;
    const notice = tenants.filter((item) => item.status === "Notice").length;
    const late = tenants.filter((item) => item.status === "Late").length;
    const rentRoll = tenants.reduce((sum, item) => sum + Number(item.monthly_rent || 0), 0);

    return {
      total: tenants.length,
      active,
      notice,
      late,
      rentRoll,
    };
  }, [tenants]);

  const filteredTenants = useMemo(() => {
    return tenants.filter((tenant) => {
      const matchesQuery =
        tenant.full_name?.toLowerCase().includes(query.toLowerCase()) ||
        tenant.property_name?.toLowerCase().includes(query.toLowerCase()) ||
        tenant.unit?.toLowerCase().includes(query.toLowerCase()) ||
        tenant.email?.toLowerCase().includes(query.toLowerCase());

      const matchesStatus =
        statusFilter === "All" || tenant.status === statusFilter;

      return matchesQuery && matchesStatus;
    });
  }, [tenants, query, statusFilter]);

  if (loading) {
    return (
      <AppLayout
        isDark={isDark}
        setIsDark={setIsDark}
        currency={currency}
        setCurrency={setCurrency}
        eyebrow="Occupancy Control"
        title="Tenants"
        description="Manage tenant records, lease details, occupancy, and rent roll across your portfolio."
      >
        <div className={isDark ? "text-slate-300" : "text-slate-600"}>
          Loading tenants...
        </div>
      </AppLayout>
    );
  }

  return (
    <AppLayout
      isDark={isDark}
      setIsDark={setIsDark}
      currency={currency}
      setCurrency={setCurrency}
      eyebrow="Occupancy Control"
      title="Tenants"
      description="Manage tenant records, lease details, occupancy, and rent roll across your portfolio."
      overlayActive={isCreateOpen || !!editingTenant}
      actions={
        <button
          onClick={() => setIsCreateOpen(true)}
          className="inline-flex h-12 items-center justify-center rounded-2xl bg-orange-500 px-5 font-medium text-white transition hover:bg-orange-600"
        >
          <Plus className="mr-2 h-4 w-4" />
          Add Tenant
        </button>
      }
    >
      <section className="grid gap-4 md:grid-cols-2 2xl:grid-cols-4">
        {[
          {
            label: "Total tenants",
            value: `${totals.total}`,
            icon: Users,
            tone: "bg-sky-100 text-sky-700",
          },
          {
            label: "Active",
            value: `${totals.active}`,
            icon: CheckCircleIcon,
            tone: "bg-emerald-100 text-emerald-700",
          },
          {
            label: "On notice",
            value: `${totals.notice}`,
            icon: CalendarDays,
            tone: "bg-orange-100 text-orange-700",
          },
          {
            label: "Rent roll",
            value: formatMoney(totals.rentRoll, currency),
            icon: Wallet,
            tone: "bg-amber-100 text-amber-700",
          },
        ].map((item, index) => {
          const Icon = item.icon;

          return (
            <motion.div
              key={item.label}
              initial={{ opacity: 0, y: 14 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3, delay: index * 0.05 }}
              className={`rounded-[2rem] border p-5 shadow-md transition-shadow hover:shadow-lg ${
                isDark ? "border-slate-800 bg-slate-900" : "border-slate-200 bg-white"
              }`}
            >
              <div className="flex items-start justify-between">
                <div>
                  <p className={`text-sm ${isDark ? "text-slate-400" : "text-slate-500"}`}>
                    {item.label}
                  </p>
                  <h3 className={`mt-3 text-3xl font-semibold tracking-tight ${isDark ? "text-white" : "text-slate-900"}`}>
                    {item.value}
                  </h3>
                </div>

                <div className={`min-h-[92px]rounded-2xl p-3 ${item.tone}`}>
                  <Icon className="h-6 w-6" />
                </div>
              </div>
            </motion.div>
          );
        })}
      </section>

      <section
        className={`rounded-[2rem] border p-4 shadow-sm md:p-5 ${
          isDark ? "border-slate-800 bg-slate-900" : "border-slate-200 bg-white"
        }`}
      >
        <div className="flex flex-col gap-4 xl:flex-row xl:items-center xl:justify-between">
          <div className="flex flex-1 flex-col gap-3 md:flex-row md:items-center">
            <div className="relative w-full max-w-md">
              <Search className="absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-500" />
              <Input
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search by tenant, property, unit, or email..."
                className={`h-12 pl-11 pr-4 ${
                  isDark
                    ? "border-slate-700 bg-slate-950 text-white placeholder:text-slate-500"
                    : "border-slate-200 bg-white text-slate-800 placeholder:text-slate-500"
                }`}
              />
            </div>

            <div className="flex items-center gap-2">
              {["All", "Active", "Notice", "Late"].map((status) => (
                <button
                  key={status}
                  onClick={() => setStatusFilter(status)}
                  className={`rounded-2xl px-4 py-2 text-sm font-medium transition ${
                    statusFilter === status
                      ? "bg-sky-100 text-sky-700"
                      : isDark
                      ? "bg-slate-800 text-slate-300 hover:bg-slate-700"
                      : "bg-slate-50 text-slate-600 hover:bg-slate-100"
                  }`}
                >
                  {status}
                </button>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="grid gap-5 xl:grid-cols-2">
        {filteredTenants.length === 0 ? (
          <div
            className={`rounded-3xl border p-8 text-center ${
              isDark ? "border-slate-800 bg-slate-900 text-slate-300" : "border-slate-200 bg-white text-slate-600"
            }`}
          >
            No tenants found.
          </div>
        ) : (
          filteredTenants.map((tenant, index) => (
            <TenantCard
              key={tenant.id}
              tenant={tenant}
              index={index}
              isDark={isDark}
              currency={currency}
              onViewTenant={setViewingTenant}
              onManageLease={setManagingLease}
              onEdit={setEditingTenant}
              onDelete={setDeletingTenant}
            />
          ))
        )}
      </section>

      <TenantFormModal
        isOpen={isCreateOpen}
        mode="create"
        onClose={() => setIsCreateOpen(false)}
        onSaved={fetchTenants}
        isDark={isDark}
        currency={currency}
        currentOrganizationId={currentOrganizationId}
        isGrowthUser={isGrowthUser}
      />

      <TenantProfileModal
        isOpen={!!viewingTenant}
        tenant={viewingTenant}
        onClose={() => setViewingTenant(null)}
        isDark={isDark}
        currency={currency}
        currentOrganizationId={currentOrganizationId}
        isGrowthUser={isGrowthUser}
      />

      <LeaseModal
        isOpen={!!managingLease}
        tenant={managingLease}
        onClose={() => setManagingLease(null)}
        onSaved={fetchTenants}
        isDark={isDark}
        currency={currency}
        currentOrganizationId={currentOrganizationId}
        isGrowthUser={isGrowthUser}
      />

      <TenantFormModal
        isOpen={!!editingTenant}
        mode="edit"
        tenant={editingTenant}
        onClose={() => setEditingTenant(null)}
        onSaved={fetchTenants}
        isDark={isDark}
        currency={currency}
        currentOrganizationId={currentOrganizationId}
        isGrowthUser={isGrowthUser}
      />

      <DeleteTenantModal
        isOpen={!!deletingTenant}
        tenant={deletingTenant}
        onClose={() => setDeletingTenant(null)}
        onDeleted={fetchTenants}
        isDark={isDark}
      />
    </AppLayout>
  );
}

function CheckCircleIcon(props) {
  return <Users {...props} />;
}
